import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { usePageTitle } from '@hooks/usePageTitle.ts';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { useConditionalDebounce } from '@hooks/useDebounceConditional.tsx';
import { useLocalStorage } from 'primereact/hooks';
import PermissionError from '@components/layout/PermissionError.tsx';
import { PermissionTypesEnum } from '@mock/Permissions.ts';
import { MenuItemModel } from '@components/layout/menu/types.ts';
import SvgOrganizations from '@components/icons/admin/Organizations.tsx';
import SvgArticles from '@components/icons/admin/Articles.tsx';
import SvgUsers from '@components/icons/admin/Users.tsx';
import SvgProducts from '@components/icons/admin/Products.tsx';
import SvgCategories from '@components/icons/admin/Categories.tsx';
import SvgAuctions from '@components/icons/admin/Auctions.tsx';
import SvgOrders from '@components/icons/admin/Orders.tsx';
import SvgSettings from '@components/icons/admin/Settings.tsx';
import MenuHeader from '@components/layout/menu/MenuHeader.tsx';
import Menu from '@components/layout/menu/Menu.tsx';
import SvgMenuUser from '@components/icons/admin/MenuUser.tsx';
import { useUser } from '@hooks/useUser.ts';
import { useAuthStore } from '@stores/authStore.ts';
import { IconWrapper } from '@components/icons/IconWrapper.tsx';
import { TopInformation } from '@components/layout/TopInformation.tsx';
import { useTranslation } from 'react-i18next';
import { useRouterState } from '@tanstack/react-router';
import { useWidth } from '@hooks/useWidth.ts';
import { useEscapeKey } from '@hooks/useEscapeKey.ts';
import { useIsTouchDevice } from '@hooks/useIsTouchDevice.ts';

/**
 * Pod touto sirkou (tablet na vysku, telefon) je menu vysuvny panel s hamburgerem. Vysuvny
 * panel je i na kazdem dotykovem zarizeni bez mysi (iPad na sirku ma 1024–1366 px) — puvodni
 * bocni menu se zasouva najetim mysi, coz na dotyku nefunguje, a zabira 270 px.
 */
const DRAWER_BREAKPOINT = 1024;

/** Jak dlouho po otevreni panelu ignorovat klik na prekryv (dozvuk tuknuti na hamburger). */
const OVERLAY_CLICK_GUARD_MS = 400;

interface ComponentProps {
  title?: string;
  oneOfPermission?: PermissionTypesEnum[];
}

const hasAnyPermission = (userPermissions: string[] | undefined, required: PermissionTypesEnum[]): boolean => {
  if (!userPermissions) return false;
  return required.some(p => userPermissions.includes(p));
};

const AdminLayout: React.FC<PropsWithChildren<ComponentProps>> = ({ title, oneOfPermission, children }) => {
  // Titulek tabu plni <PageTitle> v rootu; odsud mu jen podstrcime nazev admin stranky.
  usePageTitle(title);
  const { t } = useTranslation('admin');
  const [smallMenuForDebounce, setSmallMenu] = useState(false);
  const smallMenu = useConditionalDebounce<boolean>(smallMenuForDebounce, 700, t2 => !t2);
  const { beUser } = useUser();

  const loggedUser = beUser;
  const userPermissions = beUser?.permissions;
  const hasPermission = !oneOfPermission || hasAnyPermission(userPermissions, oneOfPermission);

  const authLogout = useAuthStore(s => s.logout);
  const logout = () => {
    authLogout();
  };

  const [lockedMenu, setLockedMenu] = useLocalStorage(true, 'Layout.lockedMenuAdmin');

  const [visible] = useState(false);

  // Tablet/telefon: menu jako vysuvny panel. Hover tam neexistuje, takze zasouvani mysi
  // a zamek menu davaji smysl jen na desktopu.
  const isTouch = useIsTouchDevice();
  const isDrawer = useWidth() < DRAWER_BREAKPOINT || isTouch;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openedAt = useRef(0);
  const openDrawer = () => {
    openedAt.current = Date.now();
    setDrawerOpen(true);
  };
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  // Safari na iPadu po tuknuti na hamburger muze dorucit klik i na prekryv, ktery se pod prstem
  // prave objevil — panel by se hned zase zavrel. Kliky na prekryv tesne po otevreni ignorujeme.
  const closeDrawerFromOverlay = () => {
    if (Date.now() - openedAt.current > OVERLAY_CLICK_GUARD_MS) closeDrawer();
  };
  // Zavrit jen pri navigaci. Ne pri zmene sirky/detekce dotyku — iPad meni velikost okna
  // (schovani listy Safari) i media queries za behu a panel by se sam zaviral.
  const pathname = useRouterState({ select: s => s.location.pathname });
  useEffect(() => setDrawerOpen(false), [pathname]);
  useEscapeKey(isDrawer && drawerOpen, closeDrawer);

  if (!hasPermission)
    return (
      <>
        <PermissionError permission={oneOfPermission ?? []} />
      </>
    );

  const hp = (...perms: PermissionTypesEnum[]) => hasAnyPermission(userPermissions, perms);

  const appMenu: MenuItemModel[] = [
    {
      title: t('menu.organizations'),
      render: hp(PermissionTypesEnum.ADMIN_ORGANIZATION_READ, PermissionTypesEnum.ADMIN_DELIVERY_WINDOW_READ, PermissionTypesEnum.ADMIN_CONTRACT_GROUP_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgOrganizations />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.organizations'), render: hp(PermissionTypesEnum.ADMIN_ORGANIZATION_READ), url: '/admin/organizations' },
        { title: t('menu.customers'), render: hp(PermissionTypesEnum.ADMIN_ORGANIZATION_READ), url: '/admin/customers' },
        {
          title: t('menu.organizationMerge'),
          render: hp(PermissionTypesEnum.ADMIN_ORGANIZATION_MERGE),
          url: '/admin/organization-merge',
        },
        {
          title: t('menu.contractGroups'),
          render: hp(PermissionTypesEnum.ADMIN_CONTRACT_GROUP_READ),
          url: '/admin/contract-groups',
        },
        {
          title: t('menu.deliveryWindows'),
          render: hp(PermissionTypesEnum.ADMIN_DELIVERY_WINDOW_READ),
          url: '/admin/delivery-windows',
        },
      ],
    },
    {
      title: t('menu.articles'),
      render: hp(PermissionTypesEnum.ADMIN_ARTICLE_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgArticles />
        </IconWrapper>
      ),
      subMenu: [{ title: t('menu.articles'), render: hp(PermissionTypesEnum.ADMIN_ARTICLE_READ), url: '/admin/articles' }],
    },
    {
      title: t('menu.users'),
      render: hp(
        PermissionTypesEnum.ADMIN_USER_READ,
        PermissionTypesEnum.ADMIN_ROLE_READ,
        PermissionTypesEnum.ADMIN_SECURITY_GROUP_READ,
        PermissionTypesEnum.ADMIN_SESSION_READ,
      ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgUsers />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.users'), render: hp(PermissionTypesEnum.ADMIN_USER_READ), url: '/admin/users' },
        {
          title: t('menu.roles'),
          render: hp(PermissionTypesEnum.ADMIN_ROLE_READ),
          url: '/admin/roles',
        },
        { title: t('menu.securityGroups'), render: hp(PermissionTypesEnum.ADMIN_SECURITY_GROUP_READ), url: '/admin/security-groups' },
        { title: t('menu.activeSessions'), render: hp(PermissionTypesEnum.ADMIN_SESSION_READ), url: '/admin/sessions' },
      ],
    },
    {
      title: t('menu.categories'),
      render: hp(PermissionTypesEnum.ADMIN_CATEGORY_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgCategories />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.categories'), render: hp(PermissionTypesEnum.ADMIN_CATEGORY_READ), url: '/admin/categories' },
        { title: t('menu.categoryTree'), render: hp(PermissionTypesEnum.ADMIN_CATEGORY_READ), url: '/admin/category-tree' },
      ],
    },
    {
      title: t('menu.productsAndPriceLists'),
      render: hp(PermissionTypesEnum.ADMIN_PRODUCT_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgProducts />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.products'), render: hp(PermissionTypesEnum.ADMIN_PRODUCT_READ), url: '/admin/products' },
        { title: t('menu.productImages'), render: hp(PermissionTypesEnum.ADMIN_PRODUCT_READ), url: '/admin/product-images' },
        { title: t('menu.imageImport'), render: hp(PermissionTypesEnum.ADMIN_PRODUCT_READ), url: '/admin/image-import' },
      ],
    },
    {
      title: t('menu.orders'),
      render: hp(PermissionTypesEnum.ADMIN_ORDER_READ, PermissionTypesEnum.ADMIN_CART_READ, PermissionTypesEnum.ADMIN_REQUISITION_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgOrders />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.orders'), render: hp(PermissionTypesEnum.ADMIN_ORDER_READ), url: '/admin/orders' },
        { title: t('menu.requisitions'), render: hp(PermissionTypesEnum.ADMIN_REQUISITION_READ), url: '/admin/requisitions' },
        { title: t('menu.failedExports'), render: hp(PermissionTypesEnum.ADMIN_ORDER_READ), url: '/admin/order-exports' },
        { title: t('menu.invoicingExports'), render: hp(PermissionTypesEnum.ADMIN_ORDER_READ), url: '/admin/invoicing-exports' },
        { title: t('menu.reportingExports'), render: hp(PermissionTypesEnum.ADMIN_ORDER_READ), url: '/admin/reporting-exports' },
        { title: t('menu.workflows'), render: hp(PermissionTypesEnum.ADMIN_ORDER_READ), url: '/admin/workflows' },
        { title: t('menu.carts'), render: hp(PermissionTypesEnum.ADMIN_CART_READ), url: '/admin/carts' },
      ],
    },
    {
      title: t('menu.auctions'),
      render: hp(PermissionTypesEnum.ADMIN_AUCTION_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgAuctions />
        </IconWrapper>
      ),
      subMenu: [{ title: t('menu.auctions'), render: hp(PermissionTypesEnum.ADMIN_AUCTION_READ), url: '/admin/auctions' }],
    },
    {
      title: t('menu.emails'),
      render: hp(PermissionTypesEnum.ADMIN_EMAIL_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgSettings />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.emailRequests'), render: hp(PermissionTypesEnum.ADMIN_EMAIL_READ), url: '/admin/email-requests' },
        { title: t('menu.resolvedEmails'), render: hp(PermissionTypesEnum.ADMIN_EMAIL_READ), url: '/admin/resolved-emails' },
      ],
    },
    {
      title: t('menu.demoInvitations'),
      render: hp(PermissionTypesEnum.ADMIN_DEMO_INVITE_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgSettings />
        </IconWrapper>
      ),
      subMenu: [{ title: t('menu.demoInvitations'), render: hp(PermissionTypesEnum.ADMIN_DEMO_INVITE_READ), url: '/admin/demo-invitations' }],
    },
    {
      title: t('menu.eshopExtraction'),
      render: hp(PermissionTypesEnum.ADMIN_ESHOP_EXTRACTION),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgSettings />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.eshopExtractors'), render: hp(PermissionTypesEnum.ADMIN_ESHOP_EXTRACTION), url: '/admin/eshop-extraction' },
        { title: t('menu.eshopExtractedProducts'), render: hp(PermissionTypesEnum.ADMIN_ESHOP_EXTRACTION), url: '/admin/eshop-extraction/products' },
      ],
    },
    {
      title: t('menu.sync'),
      render: hp(
        PermissionTypesEnum.ADMIN_SYNC_REQUEST_READ,
        PermissionTypesEnum.ADMIN_SYNC_AUDIT_READ,
        PermissionTypesEnum.ADMIN_SYNC_DIFF_READ,
        PermissionTypesEnum.ADMIN_ENV_CHECK_READ,
        PermissionTypesEnum.ADMIN_SYNC_SEND,
        PermissionTypesEnum.ADMIN_INITIAL_LOAD_READ,
        PermissionTypesEnum.ADMIN_LOAD_NP_DATA,
        PermissionTypesEnum.ADMIN_CATALOG_IMPORT,
        PermissionTypesEnum.ADMIN_DELIVERY_IMPORT,
        PermissionTypesEnum.ADMIN_PRICE_CORRECTION,
        PermissionTypesEnum.ADMIN_INITIAL_LOAD_IMPORT_SUPPLIERS,
        PermissionTypesEnum.ADMIN_NP_SYNC_USERS,
        PermissionTypesEnum.ADMIN_NP_SYNC_ADDRESSES,
      ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgSettings />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.syncRequests'), render: hp(PermissionTypesEnum.ADMIN_SYNC_REQUEST_READ), url: '/admin/sync-requests' },
        { title: t('menu.syncResult'), render: hp(PermissionTypesEnum.ADMIN_SYNC_AUDIT_READ), url: '/admin/sync-result' },
        { title: t('menu.syncBatches'), render: hp(PermissionTypesEnum.ADMIN_SYNC_AUDIT_READ), url: '/admin/sync-batches' },
        { title: t('menu.initialLoad'), render: hp(PermissionTypesEnum.ADMIN_INITIAL_LOAD_READ), url: '/admin/initial-load' },
        { title: t('menu.syncDiff'), render: hp(PermissionTypesEnum.ADMIN_SYNC_DIFF_READ), url: '/admin/sync-diff' },
        { title: t('menu.envCheck'), render: hp(PermissionTypesEnum.ADMIN_ENV_CHECK_READ), url: '/admin/env-check' },
        { title: t('menu.syncSend'), render: hp(PermissionTypesEnum.ADMIN_SYNC_SEND), url: '/admin/sync-send' },
        { title: t('menu.npLoad'), render: hp(PermissionTypesEnum.ADMIN_LOAD_NP_DATA), url: '/admin/np-load' },
        { title: t('menu.catalogImport'), render: hp(PermissionTypesEnum.ADMIN_CATALOG_IMPORT), url: '/admin/catalog-import' },
        { title: t('menu.deliveryImport'), render: hp(PermissionTypesEnum.ADMIN_DELIVERY_IMPORT), url: '/admin/delivery-import' },
        { title: t('menu.priceCorrection'), render: hp(PermissionTypesEnum.ADMIN_PRICE_CORRECTION), url: '/admin/price-correction' },
        {
          title: t('menu.organizationSync'),
          render: hp(PermissionTypesEnum.ADMIN_INITIAL_LOAD_IMPORT_SUPPLIERS, PermissionTypesEnum.ADMIN_INITIAL_LOAD_IMPORT_ORGANIZATIONS),
          url: '/admin/organization-sync',
        },
        { title: t('menu.supplierReid'), render: hp(PermissionTypesEnum.ADMIN_INITIAL_LOAD_IMPORT_SUPPLIERS), url: '/admin/supplier-reid' },
        { title: t('menu.npUserSync'), render: hp(PermissionTypesEnum.ADMIN_NP_SYNC_USERS), url: '/admin/np-user-sync' },
        { title: t('menu.npAddressSync'), render: hp(PermissionTypesEnum.ADMIN_NP_SYNC_ADDRESSES), url: '/admin/np-address-sync' },
      ],
    },
    {
      title: t('menu.settings'),
      render: hp(PermissionTypesEnum.ADMIN_TASK_READ, PermissionTypesEnum.ADMIN_ELASTIC_READ, PermissionTypesEnum.ADMIN_REPORT_READ),
      mode: 'open-inside',
      icon: (
        <IconWrapper className="h-5 w-5">
          <SvgSettings />
        </IconWrapper>
      ),
      subMenu: [
        { title: t('menu.reports'), render: hp(PermissionTypesEnum.ADMIN_REPORT_READ), url: '/admin/reports' },
        { title: t('menu.tasks'), render: hp(PermissionTypesEnum.ADMIN_TASK_READ), url: '/admin/tasks' },
        { title: t('menu.reindexElastic'), render: hp(PermissionTypesEnum.ADMIN_ELASTIC_READ), url: '/admin/elastic' },
        { title: t('menu.esQueryLogs'), render: hp(PermissionTypesEnum.ADMIN_ELASTIC_READ), url: '/admin/es-query-logs' },
      ],
    },
  ] as MenuItemModel[];

  const userMenu = [
    {
      title: (
        <div>
          <div>
            {loggedUser?.firstName} {loggedUser?.familyName}
          </div>
          <div className={'flex flex-row'}>{loggedUser?.email}</div>
        </div>
      ),
      icon: (
        <IconWrapper className={'h-7 w-7'}>
          <SvgMenuUser />
        </IconWrapper>
      ),
      mode: 'open-outside-bottom',
      render: true,
      subMenu: [
        {
          title: t('menu.profile'),
          url: '/profile',
          render: true,
        },
        {
          title: t('menu.logout'),
          onClick: () => logout(),
          render: true,
        },
      ],
    },
  ] as MenuItemModel[];

  if (isNullOrUndefined(loggedUser)) <></>;

  return (
    <>
      <TopInformation />
      {/* Plovoucí indikátory úloh */}
      {/*<TaskIndicators />*/}
      <div className={'h-screen flex flex-row bg-surface-lighter-neutral'}>
        {isDrawer && drawerOpen && <div className={'fixed inset-0 z-40 bg-black/40 cursor-pointer'} onClick={closeDrawerFromOverlay} aria-hidden="true" />}
        <aside
          className={twMerge(
            clsx(
              'min-h-screen justify-between flex flex-col w-[270px] duration-150 bg-white shrink-0',
              { 'w-[80px]': !isDrawer && smallMenu && !lockedMenu },
              // Vysuvny panel: mimo tok stranky, zasunuty doleva, vysune se hamburgerem
              { 'fixed inset-y-0 left-0 z-50 h-full min-h-0 max-w-[85vw] shadow-xl transition-transform': isDrawer },
              { '-translate-x-full': isDrawer && !drawerOpen },
            ),
          )}
          onMouseOut={() => {
            if (!isDrawer) setSmallMenu(true);
          }}
          onMouseOver={() => {
            if (!isDrawer && !visible) setSmallMenu(false);
          }}
        >
          <div className={'flex flex-col grow  overflow-auto scrollbar-visible'}>
            <MenuHeader
              smallMenu={isDrawer ? false : smallMenu}
              lockedMenu={isDrawer ? true : lockedMenu}
              setLockedMenu={setLockedMenu}
              onClose={isDrawer ? closeDrawer : undefined}
            />
            <div className={'flex flex-col grow overflow-auto scrollbar-visible'}>
              <Menu menu={appMenu} smallMenu={!isDrawer && smallMenu} lockedMenu={isDrawer || lockedMenu} localStorageKey={'AdminLayout.menu1'} />
            </div>
          </div>
          <div className={'mb-5'}>
            <Menu menu={userMenu} smallMenu={!isDrawer && smallMenu} lockedMenu={isDrawer || lockedMenu} localStorageKey={'AdminLayout.menu2'} />
          </div>
        </aside>
        <main className={'flex-1 flex flex-col overflow-hidden w-full min-w-0'}>
          {isDrawer && (
            <header className={'flex items-center gap-3 h-14 px-3 bg-white border-b border-gray-200 shrink-0'}>
              <button
                type="button"
                aria-label="Otevřít menu"
                aria-expanded={drawerOpen}
                onClick={openDrawer}
                className={'p-2 -ml-1 rounded text-gray-700 hover:bg-gray-100'}
              >
                <svg viewBox="0 0 24 24" className={'h-6 w-6'} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              {title && <span className={'font-semibold text-gray-900 truncate'}>{title}</span>}
            </header>
          )}
          <div
            className={clsx('admin-content flex-1 overflow-y-scroll pl-0.5 pb-0.5 pr-0.5 gap-2 flex-col scrollbar-visible min-w-0 mx-2 md:mx-4', {
              'pt-3': isDrawer,
            })}
          >
            {children}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminLayout;
